//// Fetching JSON files on slow / unstable connections
// - an attempt is given up when nothing has been received for `stallTimeout` ms (a slow download that still progresses is never cut)
// - attempts that failed because of the connection (network error, stalled download, server error) are retried, with an increasing delay
// - a file requested again while it is downloading (or shortly after) is not downloaded twice: the callers share the same result,
//   which they must therefore not modify
// - the server is asked on every request whether the file changed (it then only answers "not modified" when it did not):
//   an old copy kept by the browser is never displayed, whatever the server says about how long its files may be reused
// - `low: true` holds the request back until the other requests of this module are over (bounded by LOW_MAX_WAIT),
//   so that a big file does not slow down the small ones the interface is waiting for
// The error of a connection failure carries `connection: true` (a missing or malformed file does not, and is not retried).
const SHARE_MS = 30000
const LOW_MAX_WAIT = 15000
const shared = new Map() // url -> promise of the parsed file
let active = 0
let idleWaiters = []

export function fetchJSON(url, {stallTimeout = 20000, retries = 2, low = false} = {}) {
    if (shared.has(url)) return shared.get(url)

    const attempt = (n) => fetchJSONOnce(url, stallTimeout).catch(err => {
        if (!err.connection || n >= retries) throw err
        return new Promise(resolve => setTimeout(resolve, 1000 * 2 ** n)).then(() => attempt(n + 1))
    })
    const start = () => {
        active++
        return attempt(0).finally(() => {
            active--
            if (active === 0) idleWaiters.splice(0).forEach(resolve => resolve())
        })
    }

    const promise = low ? whenIdle().then(start) : start()
    shared.set(url, promise)
    promise.then(() => setTimeout(() => shared.delete(url), SHARE_MS), () => shared.delete(url))
    return promise
}

function whenIdle() {
    if (active === 0) return Promise.resolve()
    return new Promise(resolve => {
        idleWaiters.push(resolve)
        setTimeout(resolve, LOW_MAX_WAIT)
    })
}

function fetchJSONOnce(url, stallTimeout) {
    const controller = new AbortController()
    let timer
    const restartTimer = () => {
        clearTimeout(timer)
        timer = setTimeout(() => controller.abort(), stallTimeout)
    }
    const connectionError = (err) => {
        err.connection = true
        throw err
    }

    restartTimer()
    return fetch(url, {signal: controller.signal, cache: 'no-cache'})
        .then(resp => {
            if (!resp.ok) {
                const err = new Error(`HTTP ${resp.status} while fetching ${url}`)
                err.connection = resp.status >= 500
                throw err
            }
            if (!resp.body) return resp.text().catch(connectionError)

            // reading the body chunk by chunk, each chunk received restarts the timer
            const reader = resp.body.getReader()
            const decoder = new TextDecoder()
            let text = ''
            const read = () => reader.read().then(({done, value}) => {
                if (done) return text + decoder.decode()
                restartTimer()
                text += decoder.decode(value, {stream: true})
                return read()
            })
            return read().catch(connectionError)
        }, connectionError)
        .then(text => JSON.parse(text))
        .finally(() => clearTimeout(timer))
}
